import { useCallback, useState } from 'react';
import { validate, validateFields, FieldSpec } from './engine';

export interface FieldValidationApi {
  errors: Record<string, string>;
  /** Validate one field (call on blur). Returns true if valid. */
  validateField: (name: string, code: number, value: any,
                  opts?: { additional?: Record<string, any> | null; required?: boolean }) => boolean;
  /** Clear a field's error (call on change, so the message hides while editing). */
  clearError: (name: string) => void;
  /** Programmatically set (or clear) a field's error — used for domain checks that must coexist with engine format errors. */
  setError: (name: string, message?: string) => void;
  /** Validate a whole spec list (call on submit). Sets all errors; returns true if all valid. */
  validateAll: (specs: FieldSpec[]) => boolean;
  /** Drop all errors (call on form open/reset). */
  reset: () => void;
}

export function useFieldValidation(): FieldValidationApi {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField: FieldValidationApi['validateField'] = useCallback((name, code, value, opts) => {
    const required = opts?.required ?? false;
    const isEmpty = value == null || (typeof value === 'string' && value.trim() === '');
    let message: string | undefined;
    if (isEmpty && required) message = 'This field is required';
    else if (!isEmpty) {
      const r = validate(code, value, opts?.additional);
      if (!r.valid) message = r.message;
    }
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[name] = message; else delete next[name];
      return next;
    });
    return !message;
  }, []);

  const clearError = useCallback((name: string) => {
    setErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev }; delete next[name]; return next;
    });
  }, []);

  const setError = useCallback((name: string, message?: string) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[name] = message; else delete next[name];
      return next;
    });
  }, []);

  const validateAll = useCallback((specs: FieldSpec[]) => {
    const errs = validateFields(specs);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, []);

  const reset = useCallback(() => setErrors({}), []);

  return { errors, validateField, clearError, setError, validateAll, reset };
}
